/**
 * Offline Incident Form Component
 * Allows creating incidents that work offline and sync when online
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Box,
  Alert,
  IconButton,
  Chip,
  Autocomplete
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudOff as CloudOffIcon,
  CloudUpload as CloudUploadIcon,
  MyLocation as MyLocationIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { createIncident } from '../services/offlineApi';
import { isOnline } from '../services/syncManager';
import { INCIDENT_TYPES } from '../constants/incidentOptions';

// Common Texas freeway options (cached for offline use)
const FREEWAY_OPTIONS = [
  'I-35E NB',
  'I-35E SB',
  'I-35W NB',
  'I-35W SB',
  'US-75 NB',
  'US-75 SB',
  'US-175 EB',
  'US-175 WB',
  'SH-121 NB',
  'SH-121 SB',
  'I-30 EB',
  'I-30 WB',
  'I-20 EB',
  'I-20 WB',
  'I-45 NB',
  'I-45 SB',
  'SH-183 EB',
  'SH-183 WB',
  'I-635 EB',
  'I-635 WB',
];

const OfflineIncidentForm = ({ open, onClose, onSuccess, token, currentLocation }) => {
  const [online, setOnline] = useState(navigator.onLine);
  const [saving, setSaving] = useState(false);
  const saveFlight = useRef(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Form fields
  const [incidentType, setIncidentType] = useState('');
  const [freeway, setFreeway] = useState('');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [latitude, setLatitude] = useState(currentLocation?.lat || '');
  const [longitude, setLongitude] = useState(currentLocation?.lng || '');
  const [useGps, setUseGps] = useState(true);

  // Track online status
  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Update GPS when current location changes
  useEffect(() => {
    if (useGps && currentLocation) {
      setLatitude(currentLocation.lat);
      setLongitude(currentLocation.lng);
    }
  }, [currentLocation, useGps]);

  // Get current GPS location
  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLatitude(position.coords.latitude);
          setLongitude(position.coords.longitude);
          setUseGps(true);
        },
        (error) => {
          console.error('GPS error:', error);
          setError('Unable to get GPS location');
        },
        { enableHighAccuracy: true }
      );
    } else {
      setError('GPS not available on this device');
    }
  };

  // Reset form
  const resetForm = () => {
    setIncidentType('');
    setFreeway('');
    setLocation('');
    setDescription('');
    setNotes('');
    setLatitude(currentLocation?.lat || '');
    setLongitude(currentLocation?.lng || '');
    setError(null);
    setSuccess(null);
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (saveFlight.current) return;
    // Validation
    if (!incidentType) {
      setError('Please select an incident type');
      return;
    }
    if (!location && !freeway) {
      setError('Please enter a location or freeway');
      return;
    }

    saveFlight.current = true;
    setSaving(true);
    setError(null);

    try {
      const incidentData = {
        source: 'OFFLINE-MDT',
        type: incidentType,
        location: location || `${freeway}`,
        description: description || `${incidentType} at ${location || freeway}`,
        notes,
        latitude: latitude ? parseFloat(latitude) : null,
        longitude: longitude ? parseFloat(longitude) : null,
        freeway,
        reportedAt: new Date().toISOString()
      };

      const result = await createIncident(incidentData, token);

      if (result.offline) {
        setSuccess(result.message + " Review Pending calls for delivery status.");
      } else {
        setSuccess(`Incident created successfully (ID: ${result.incident.id})`);
      }

      // Notify parent
      if (onSuccess) {
        onSuccess(result);
      }

      // Reset form after short delay to show success
      setTimeout(() => {
        resetForm();
        onClose();
      }, 1500);

    } catch (err) {
      console.error('Failed to save incident:', err);
      setError(`Failed to save incident: ${err.message}`);
    } finally {
      saveFlight.current = false;
      setSaving(false);
    }
  };

  // Handle close
  const handleClose = () => {
    if (!saving) {
      resetForm();
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            {online ? 'Create Incident' : 'Create Incident (Offline)'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              icon={online ? <CloudUploadIcon /> : <CloudOffIcon />}
              label={online ? 'Online' : 'Offline'}
              color={online ? 'success' : 'warning'}
              size="small"
            />
            <IconButton onClick={handleClose} disabled={saving}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {!online && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningIcon fontSize="small" />
              <Typography variant="body2">
                You're offline. This incident will be saved locally and uploaded when you reconnect.
              </Typography>
            </Box>
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Incident Type */}
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel>Incident Type</InputLabel>
              <Select
                value={incidentType}
                onChange={(e) => setIncidentType(e.target.value)}
                label="Incident Type"
              >
                {INCIDENT_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Freeway */}
          <Grid item xs={12} sm={6}>
            <Autocomplete
              freeSolo
              options={FREEWAY_OPTIONS}
              value={freeway}
              onChange={(e, value) => setFreeway(value || '')}
              onInputChange={(e, value) => setFreeway(value || '')}
              renderInput={(params) => (
                <TextField {...params} label="Freeway" placeholder="e.g., I-35E NB" />
              )}
            />
          </Grid>

          {/* Location */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Cross Street / Mile Marker"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g., At Main St"
            />
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={2}
              placeholder="Brief description of the incident"
            />
          </Grid>

          {/* GPS Coordinates */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="subtitle2">GPS Coordinates</Typography>
              <Button
                size="small"
                startIcon={<MyLocationIcon />}
                onClick={getCurrentLocation}
              >
                Get Current Location
              </Button>
            </Box>
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Latitude"
              value={latitude}
              onChange={(e) => {
                setLatitude(e.target.value);
                setUseGps(false);
              }}
              type="number"
              inputProps={{ step: 'any' }}
              size="small"
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Longitude"
              value={longitude}
              onChange={(e) => {
                setLongitude(e.target.value);
                setUseGps(false);
              }}
              type="number"
              inputProps={{ step: 'any' }}
              size="small"
            />
          </Grid>

          {/* Notes */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Additional Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              rows={2}
              placeholder="Any additional information"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
          startIcon={online ? <CloudUploadIcon /> : <CloudOffIcon />}
        >
          {saving 
            ? 'Saving...' 
            : online 
              ? 'Create Incident' 
              : 'Save Offline'
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OfflineIncidentForm;
